import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";

import { PageHeader, Card, showSnackbar, Table, Checkbox } from "@cofano/cck";
import { Box, useTheme } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import { getMultiValuesField } from "components/utils/Permission";
import { TableId, useFrontendTable } from "components/table";

import CostTypeSelect, { CostTypeSelectOption } from "view/costtypes/components/selects/CostTypeSelect";
import InvoiceGroupTypeSelect, { InvoiceGroupTypeSelectOption } from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import VatCodeSelect, { VatCodeSelectOption } from "view/vatcodes/components/selects/VatCodeSelect";

import { Language, hasOptionalFieldPermission, Fields } from "imports/Utils";

import { ChildPropsFromArray } from "types/commons";

import {
	GetAgreementTypeDefaultVatCodesDocument,
	GetAgreementTypesDocument,
	GetAgreementTypesQuery,
} from "__generated__/view/agreementtypes/graphql/agreementType.queries.generated";
import {
	SetDefaultVatCodeDocument,
	UpdateAgreementTypeDocument,
	UpdateAgreementTypeOrderDocument,
} from "__generated__/view/agreementtypes/graphql/agreementType.mutations.generated";

import { AgreementTypeEnum, CharterType, VatRelation } from "__generated__/enums";

type BaseAgreementTypeFromQuery = ChildPropsFromArray<GetAgreementTypesQuery, "agreementTypes">;

type AgreementTypeFromQuery = Omit<BaseAgreementTypeFromQuery, "type"> & {
	type?: BaseAgreementTypeFromQuery["type"] | "DEFAULT" | null | undefined;
};

const AgreementTypes = () => {
	const session = useStoreState(state => state.session);

	const theme = useTheme();

	const customAgreementTypes = getMultiValuesField(session, "customAgreementTypes") ?? "";
	const customAgreements = customAgreementTypes.split(";").filter(s => s.length > 0) as AgreementTypeEnum[];

	const { loading: agreementTypesLoading, refetch: refetchAgreementTypes } = useQuery(GetAgreementTypesDocument, {
		variables: {
			search: {
				types: [ ...customAgreements ],
			},
		},
		onCompleted: (response) => {
			setContent(response.agreementTypes ?? []);
		},
	});

	const [ content, setContent ] = useState<AgreementTypeFromQuery[]>([]);

	const [ setDefaultVatCode ] = useMutation(SetDefaultVatCodeDocument);
	const [ updateAgreementType ] = useMutation(UpdateAgreementTypeDocument);
	const [ updateAgreementTypeOrder ] = useMutation(UpdateAgreementTypeOrderDocument);

	const { data: defaultVatCodesData, loading: defaultVatCodesLoading } = useQuery(GetAgreementTypeDefaultVatCodesDocument);
	const defaultVatCodes = defaultVatCodesData?.agreementTypeDefaultVatCodes;

	const defaultVatCodeRow = defaultVatCodes && {
		id: defaultVatCodes.id ?? 0,
		type: "DEFAULT",
		nationalVATCode: defaultVatCodes.nationalVATCode,
		euVATCode: defaultVatCodes.euVATCode,
		worldVATCode: defaultVatCodes.worldVATCode,
	};

	const { tableProps, refetch } = useFrontendTable({
		id: TableId.AgreementTypes,
		paginationEnabled: false,
		content: [ { ...defaultVatCodeRow }, ...content.map(at => ({ ...at, id: at.id ?? undefined })) ],
	});

	const saveDefault = (option: VatCodeSelectOption) => {
		setDefaultVatCode({
			variables: {
				id: option.id,
			},
		}).then(() => {
			refetch(0);
			showSnackbar(intl.formatMessage({ id: "ADMINISTRATION_SAVED" }), { variant: "success" });
		});
	};

	const save = (
		name: "nationalVATCode" | "euVATCode" | "worldVATCode" | "costType" | "invoiceGroupTypeSales" | "invoiceGroupTypePurchase" | "exclusiveToParentContracts",
		value: VatCodeSelectOption | CostTypeSelectOption | InvoiceGroupTypeSelectOption | boolean | null,
		agreementType: AgreementTypeFromQuery,
	) => {
		if (agreementType.type !== "DEFAULT") {
			updateAgreementType({
				variables: {
					input: {
						id: agreementType.id,
						type: agreementType.type,
						nationalVATCode: name === "nationalVATCode" ? (value as VatCodeSelectOption)?.id : agreementType.nationalVATCode?.id,
						euVATCode: name === "euVATCode" ? (value as VatCodeSelectOption)?.id : agreementType.euVATCode?.id,
						worldVATCode: name == "worldVATCode" ? (value as VatCodeSelectOption)?.id : agreementType.worldVATCode?.id,
						costType: name === "costType" ? (value as CostTypeSelectOption)?.id : agreementType.costType?.id,
						invoiceGroupTypeSales: name === "invoiceGroupTypeSales" ? (value as InvoiceGroupTypeSelectOption)?.id : agreementType.invoiceGroupTypeSales?.id,
						invoiceGroupTypePurchase: name === "invoiceGroupTypePurchase" ? (value as InvoiceGroupTypeSelectOption)?.id : agreementType.invoiceGroupTypePurchase?.id,
						exclusiveToParentContracts: name === "exclusiveToParentContracts" ? (value as boolean) : agreementType.exclusiveToParentContracts,
					},
				},
			}).then(() => {
				if (name === "exclusiveToParentContracts") {
					refetchAgreementTypes();
				}
				refetch(0);
				showSnackbar(intl.formatMessage({ id: "ADMINISTRATION_SAVED" }), { variant: "success" });
			});
		}
	};

	const onSaveOrder = (newContent: AgreementTypeFromQuery[]) => {
		updateAgreementTypeOrder({
			variables: {
				inputs: newContent.map((agreementType, index) => ({
					id: agreementType.id,
					agreementTypeOrder: index,
				})),
			},
		}).then(() => {
			refetchAgreementTypes();
			refetch(0);
			showSnackbar(intl.formatMessage({ id: "ADMINISTRATION_SAVED" }), { variant: "success" });
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("AGREEMENT_TYPES")}
					<Helmet title={intl.formatMessage({ id: "AGREEMENT_TYPES" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<Box
					sx={{
						".cck-table": {
							".tbody": {
								".tbody-row": {
									...(theme.palette.mode === "light" && {
										borderBottom: "1px solid #ddd !important",
										".cell": {
											backgroundColor: "#fff !important",
										},
									}),
								},
							},
						},
					}}>
					<Table
						{...tableProps}
						onReorder={(content) => {
							const newContent = [ ...content as AgreementTypeFromQuery[] ].filter(it => it.id !== 0);
							setContent(newContent);
							onSaveOrder(newContent);
						}}
						contentLoading={defaultVatCodesLoading || agreementTypesLoading}
						columns={[ {
							id: "type",
							title: intl.formatMessage({ id: "TYPE" }),
							render: (row: AgreementTypeFromQuery) => (
								<span style={{ fontWeight: "bold" }}>
									{intl.formatMessage({ id: `AGREEMENT_TYPE_${row.type}` })}
								</span>
							),
						}, {
							id: "nationalVATCode",
							title: intl.formatMessage({ id: "VAT_CODE_NATIONAL" }),
							render: (row: AgreementTypeFromQuery) => (
								<VatCodeSelect
									value={row.nationalVATCode}
									onChange={option => {
										if (row.type === "DEFAULT" && option) {
											saveDefault(option);
										} else {
											save("nationalVATCode", option, row);
										}
									}}
									allocation={VatRelation.BothInNation}
									clearable={row.type !== "DEFAULT"} />
							),
						}, {
							id: "euVATCode",
							title: intl.formatMessage({ id: "VAT_CODE_EU" }),
							render: (row: AgreementTypeFromQuery) => (
								<VatCodeSelect
									value={row.euVATCode}
									onChange={option => {
										if (row.type === "DEFAULT" && option) {
											saveDefault(option);
										} else {
											save("euVATCode", option, row);
										}
									}}
									allocation={VatRelation.BothInsideEu}
									clearable={row.type !== "DEFAULT"} />
							),
						}, {
							id: "worldVATCode",
							title: intl.formatMessage({ id: "VAT_CODE_WORLD" }),
							render: (row: AgreementTypeFromQuery) => (
								<VatCodeSelect
									value={row.worldVATCode}
									onChange={option => {
										if (row.type === "DEFAULT" && option) {
											saveDefault(option);
										} else {
											save("worldVATCode", option, row);
										}
									}}
									allocation={VatRelation.World}
									clearable={row.type !== "DEFAULT"} />
							),
						}, {
							id: "costType",
							title: intl.formatMessage({ id: "COST_TYPE" }),
							render: (row: AgreementTypeFromQuery) => row.type !== "DEFAULT" && (
								<CostTypeSelect
									value={row.costType}
									onChange={option => save("costType", option, row)} />
							),
						}, {
							id: "invoiceGroupTypeSales",
							title: intl.formatMessage({ id: "INVOICE_GROUP_TYPE_SALES" }),
							render: (row: AgreementTypeFromQuery) => row.type !== "DEFAULT" && (
								<InvoiceGroupTypeSelect
									value={row.invoiceGroupTypeSales}
									charterType={CharterType.Sales}
									onChange={option => save("invoiceGroupTypeSales", option, row)} />
							),
						}, {
							id: "invoiceGroupTypePurchase",
							title: intl.formatMessage({ id: "INVOICE_GROUP_TYPE_PURCHASE" }),
							render: (row: AgreementTypeFromQuery) => row.type !== "DEFAULT" && (
								<InvoiceGroupTypeSelect
									value={row.invoiceGroupTypePurchase}
									charterType={CharterType.Purchase}
									onChange={option => save("invoiceGroupTypePurchase", option, row)} />
							),
						}, {
							id: "exclusiveToParentContracts",
							title: intl.formatMessage({ id: "EXCLUSIVE_TO_PARENT_CONTRACTS" }),
							disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "parentContract"),
							render: (row: AgreementTypeFromQuery) => row.type !== "DEFAULT" && (
								<Checkbox
									checked={!!row.exclusiveToParentContracts}
									onChange={(e) => {
										setContent([ ...content ]);
										save("exclusiveToParentContracts", e.target.checked, row);
									}}
								/>
							),
						} ]} />
				</Box>
			</Card>
		</div>
	);
};

export default AgreementTypes;
